"use client";
import React from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";
import { motion } from "framer-motion";

interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}

export default function Content_Home_Layout3({ data }: ContentProps) {
    const pathname = usePathname();
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);
    const bgColor = data.bgColor || "#f4eee0";

    return (
        <section
            className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: bgColor }}
        >
            <h1>{data.title}</h1>
            {/* Background image with overlay */}
            <div className="absolute inset-0 -z-10">
                <Image
                    src={imgUrl || "/placeholder.jpg"}
                    alt={data.picture?.data?.attributes?.alternativeText || "Organic Product"}
                    fill
                    className="object-cover object-center opacity-20"
                    sizes="100vw"
                    priority
                />
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px]"></div>
            </div>

            {/* Main content */}
            <div className="w-full max-w-7xl px-6 py-20 grid md:grid-cols-2 gap-10 items-center z-10">
                {/* Left content block */}
                <motion.div
                    initial={{ opacity: 0, x: -40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col items-start text-left"
                >
                    <span className="mb-4 px-4 py-1 bg-[#fcefdc] text-[#B78D65] text-xs font-medium uppercase rounded-full">
                        100% Herbal Certified
                    </span>

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#2A2118]   leading-tight mb-6">
                        Nourish Your Hair <br />
                        <span className="italic text-[#B78D65]">Naturally</span>
                    </h1>

                    <p className="text-base sm:text-lg text-[#5A4A3A] mb-8 leading-relaxed max-w-xl">
                        Experience nature’s touch with our ethically sourced blends, crafted for visible transformation and lasting shine.
                    </p>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 text-[#5A4A3A] font-medium text-sm sm:text-base">
                        {[
                            "Zero Preservatives",
                            "Ethical Ingredients",
                            "Eco-Friendly Packaging",
                            "Lab Tested Results",
                        ].map((point, index) => (
                            <li key={index} className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-[#B78D65] rounded-full shrink-0"></span>
                                {point}
                            </li>
                        ))}
                    </ul>

                    <a
                        href="#"
                        className="inline-block relative px-8 py-3 font-medium text-white rounded-full bg-[#A67C52] hover:bg-[#8f6743] shadow-md transition-all"
                    >
                        Explore Products →
                    </a>
                </motion.div>

                {/* Right image block */}
                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative w-full h-[400px] sm:h-[500px] rounded-[32px] overflow-hidden shadow-xl"
                >
                    <Image
                        src={imgUrl || "/placeholder.jpg"}
                        alt="Herbal Product Image"
                        fill
                        className="object-cover object-center"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        priority
                    />
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
                </motion.div>
            </div>

            {/* Decorative glow */}
            <div className="absolute -bottom-20 left-10 w-72 h-72 bg-[#B78D65]/30 blur-3xl rounded-full"></div>
            <div className="absolute -top-20 right-10 w-60 h-60 bg-[#A67C52]/20 blur-2xl rounded-full"></div>
        </section>
    );
}
